import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, LogOut, ArrowRight, UserX, Trash2, LifeBuoy, Crown, Smartphone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { planLabel } from "@/data/site";

const QUICK = [
  { label: "View plans & pricing", href: "/#pricing", icon: Crown, external: false },
  { label: "Get support", href: "/support.html", icon: LifeBuoy, external: false },
  { label: "Delete my data", href: "/delete-data.html", icon: Trash2, external: false },
  { label: "Delete my account", href: "/delete-account.html", icon: UserX, external: false },
];

export default function Account() {
  const { user, loading, logout, openAuth, refreshMe } = useAuth();

  useEffect(() => {
    document.title = "My Account — Warranty_Vault pro";
    refreshMe();
  }, [refreshMe]);

  if (loading) {
    return <main className="min-h-screen pt-28" data-testid="account-loading" />;
  }

  if (!user) {
    return (
      <main className="relative min-h-screen pt-28" data-testid="account-guest">
        <div className="vault-grid-bg grain absolute inset-x-0 top-0 h-80" />
        <div className="relative mx-auto max-w-xl px-5 py-16 text-center sm:px-8">
          <Reveal>
            <p className="eyebrow">My Account</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight">You're not signed in</h1>
            <p className="mx-auto mt-4 max-w-md text-muted-foreground">
              Sign in with your Warranty_Vault pro account to see your plan and manage your data.
            </p>
            <button
              onClick={() => openAuth("login")}
              data-testid="account-login-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
            >
              Log in / Sign up <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </main>
    );
  }

  const planType = user.plan_type || "free";
  const isPaid = planType !== "free";
  const planName = planLabel(user);

  return (
    <main className="relative min-h-screen pt-28" data-testid="account-page">
      <div className="vault-grid-bg grain absolute inset-x-0 top-0 h-80" />
      <div className="relative mx-auto max-w-3xl px-5 pb-10 sm:px-8">
        <Reveal>
          <p className="eyebrow">My Account</p>
          <div className="mt-4 flex items-center gap-4">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-primary text-lg font-bold text-primary-foreground">
                {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">{user.name || "Your account"}</h1>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 rounded-2xl border border-border bg-card/50 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Current plan</p>
                <p className="mt-1 flex items-center gap-2 font-display text-xl font-bold">
                  <BadgeCheck className={`h-5 w-5 ${isPaid ? "text-gold" : "text-sage"}`} />
                  {planName}
                </p>
              </div>
              {!isPaid && (
                <Link
                  to="/#pricing"
                  data-testid="account-upgrade"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
                >
                  Upgrade <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-gold/30 bg-gold/10 p-3.5">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <p className="text-sm text-foreground/90">
                Use this same account in the Warranty_Vault pro app on your phone or tablet — everything stays in sync.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.14}>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {QUICK.map((q) => (
              <a
                key={q.href}
                href={q.href}
                data-testid={`account-link-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex items-center justify-between rounded-2xl border border-border bg-card/40 px-5 py-4 transition-colors hover:border-gold/40"
              >
                <span className="flex items-center gap-3 text-sm font-medium">
                  <q.icon className="h-4 w-4 text-gold" /> {q.label}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <button
            onClick={logout}
            data-testid="account-logout"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-destructive transition-colors hover:border-destructive/50"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </Reveal>
      </div>
    </main>
  );
}
